import type { VoidFunctionComponent } from 'react';

import BranchRevisionCard from '../../../../../../../../../generic/BranchRevisionCard/BranchRevisionCard';
import Checkbox from '../../../../../../../../../generic/Forms/Checkbox';
import List from '../../../../../../../../../generic/List';
import TooltipIcon from '../../../../../../../../../generic/TooltipIcon';
import { projectTypes } from '../../../../../../../../shared/projectTypesIconMap';
import type { Project } from '../../../../../../../../../../../shared/projectsV2';
import type { AddedProject } from '../../../../../../../../../../shared/branches/types';
import type { SelectOption } from '../../../../../../../../../../types';
import type { TeamSelectOption } from '../../../../../../../../../shared/teams/hooks/useTeamsSelectOptions';

import AddRemoveButton from './AddRemoveButton/AddRemoveButton';
import ProjectRowDetails from './ProjectRowDetails/ProjectRowDetails';

import styles from './ProjectRow.module.less';

type Props = {
  project: Project;
  addedProject?: AddedProject;
  /**
   * onToggleIsProjectSelected toggles if this project isChecked or not
   */
  onToggleIsProjectSelected: (projectLocator: Project['id']) => void;
  /**
   * True if the project row checkbox is selected
   */
  isChecked: boolean;
  onUpdateAddedProject: (project: AddedProject) => void;
  onAddProjects: (projects: Project[]) => void;
  onRemoveProjects: (projects: Project[]) => void;
  onLabelClick: (selected: SelectOption[]) => void;
  selectedLabels: SelectOption[];
  onTeamClick: (selected: TeamSelectOption[]) => void;
  selectedTeams: TeamSelectOption[];
};

const ProjectRow: VoidFunctionComponent<Props> = ({
  project,
  addedProject,
  onToggleIsProjectSelected,
  isChecked,
  onUpdateAddedProject,
  onAddProjects,
  onRemoveProjects,
  onLabelClick,
  selectedLabels,
  onTeamClick,
  selectedTeams,
}) => {
  const { title, teams, labels, issues } = project;

  const onAdd = () => onAddProjects([project]);
  const onRemove = () => onRemoveProjects([project]);
  const projectTypeIconData = projectTypes[project.type];

  return (
    <List.RowContainer>
      <List.Row
        right={
          <AddRemoveButton
            isAdded={!!addedProject}
            onAdd={onAdd}
            onRemove={onRemove}
            isDisabled={!project.latestRevision}
            tooltip="This project must be analyzed before adding to a release."
          />
        }
        isInteractive={false}
        hasCheckbox
      >
        <div className={styles.row}>
          <Checkbox
            isChecked={isChecked}
            onChange={() => onToggleIsProjectSelected(project.id)}
            isRippleDisabled
          />
          <List.RowTitle>
            <div className={styles.title}>
              <div className={styles.titleIcon}>
                <TooltipIcon
                  id={`projectTypeIcon-${projectTypeIconData.title}`}
                  icon={projectTypeIconData.icon}
                  tooltip={projectTypeIconData.title}
                />
              </div>
              {title}
            </div>
          </List.RowTitle>
          <div className={styles.details}>
            <ProjectRowDetails
              projectId={project.id}
              teams={teams}
              labels={labels}
              issues={issues}
              onLabelClick={onLabelClick}
              selectedLabels={selectedLabels}
              onTeamClick={onTeamClick}
              selectedTeams={selectedTeams}
            />
          </div>
        </div>
      </List.Row>
      {addedProject && <BranchRevisionCard addedProject={addedProject} onUpdateAddedProject={onUpdateAddedProject} />}
    </List.RowContainer>
  );
};

export default ProjectRow;
